import { Button, Card } from '@citizen-walk/shared-ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DeviceTransferConsumeForm,
  DeviceTransferModal,
  isStandalonePwa,
  useAnonymousAuth,
} from '../auth';
import { useT } from '../../i18n';
import { useMyMemberships } from './useMyMemberships';
import { useOfflineMapSummary } from './useOfflineMapSummary';

/**
 * 参加地区一覧ホーム (Sprint BG-DataSettings 9、GAP U-S1)。
 *
 * 旧 `old/apps/user/src/pages/DistrictHomePage.tsx` (381 行) を v2 で minimum 移植:
 *   - 旧 inline `getDocs` ロジック → `useMyMemberships` hook 化 (本 sprint 同時新規)
 *   - 旧 inline 「引継コード生成 (createDeviceTransferToken)」UI → Sprint BG-DataSettings 8 で
 *     実装済の `DeviceTransferModal` を再利用 (本画面では「PWA に引き継ぐ」 button 1 つで modal を開く)
 *   - 旧 inline 「引継コード入力 (consumeDeviceTransferToken)」UI → 同 Sprint 8 の
 *     `DeviceTransferConsumeForm` を再利用 (standalone 検知 + memberships 空時のみ表示)
 *   - 旧 `offlineMap` (listOfflineMaps + getStorageEstimate) badge は **本 sprint scope 外**
 *     (offlineMap 機能は v2 で別 sprint で実装、ここでは存在前提なし)
 *   - 旧 `ACCESSIBLE_DISTRICTS_LIMIT = 20` を踏襲、件数バッジ + 上限警告文言 (red/yellow/grey)
 *   - 旧 row click は Link to `/m/{districtToken}/app/map` だったが、v2 では `/m/{urlToken}` に
 *     遷移 (App.tsx 配下で AppHome が render、map 遷移は BottomTabBar から行う)
 *   - 旧 useTranslation は v2 では i18n stub なので literal string 表示 (Sprint BG i18n foundation
 *     で別途置換予定)
 *
 * route: `/` (App.tsx で `AppHome` から差し替え)。
 *
 * 注意: 本 component は DistrictProvider **配下外** (URL 直アクセスなので urlToken が未確定)。
 *  → district.id は使えない。memberships 一覧は uid のみで Firestore に query する。
 */

const ACCESSIBLE_DISTRICTS_LIMIT = 20;

export function DistrictHomePage() {
  // Sprint BG-DataSettings 19 (U-P12): 主要 4 文字列を i18n 化 (title/subtitle/list label/CTA/empty hint)。
  const t = useT();
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  const { state, refresh } = useMyMemberships(uid);
  const [deviceTransferOpen, setDeviceTransferOpen] = useState(false);
  const standalone = isStandalonePwa();
  // Phase H (old2 逐語): 参加地区 row の 🗺 オフライン地図 badge + 合計容量行用の集約状態。
  const offlineMap = useOfflineMapSummary();

  const memberships = state.memberships;
  const count = memberships.length;
  const isAtLimit = count >= ACCESSIBLE_DISTRICTS_LIMIT;
  const isNearLimit = count >= ACCESSIBLE_DISTRICTS_LIMIT - 2;
  const isLoading =
    auth.status === 'loading' || state.status === 'loading' || state.status === 'idle';

  return (
    <main className="mx-auto max-w-md p-6" data-testid="district-home-page">
      <h1 className="text-2xl font-bold mt-6 mb-2">{t('district_home.title')}</h1>
      <p className="text-sm text-slate-600 mb-6">{t('district_home.subtitle')}</p>

      {state.status === 'error' && (
        <Card padding="md" className="border-danger-300 mb-4">
          <p className="text-sm text-danger-700">
            地区一覧の取得に失敗しました: {state.error.message}
          </p>
          <div className="mt-2">
            <Button variant="secondary" size="sm" onClick={refresh}>
              再試行
            </Button>
          </div>
        </Card>
      )}

      {isLoading && state.status !== 'error' && (
        <p className="text-slate-500 mt-12 text-center" data-testid="district-home-loading">
          読み込み中…
        </p>
      )}

      {/* ブラウザ表示で参加済み: 「PWA に引き継ぐ」誘導 (旧 L194-247 踏襲、生成 UI 自体は DeviceTransferModal に委譲) */}
      {!isLoading && !standalone && count > 0 && (
        <section
          className="mb-6 bg-warning-50 border border-warning-300 rounded-md p-3"
          aria-labelledby="device-transfer-cta-heading"
          data-testid="device-transfer-cta"
        >
          <h3 id="device-transfer-cta-heading" className="font-medium text-warning-900 mb-1">
            📲 ホーム画面（PWA）に引き継ぐ
          </h3>
          <p className="text-xs text-warning-800 mb-3">
            ホーム画面に追加すると新しいアカウント扱いになります（iOS / Android 共通の PWA 仕様）。
            参加済みの地区を引き継ぐには、下のボタンで <strong>引き継ぎコード</strong> を生成し、
            ホーム画面追加後に PWA 側で入力してください（10 分有効・1 回限り）。
          </p>
          <Button
            variant="primary"
            size="md"
            onClick={() => setDeviceTransferOpen(true)}
            className="w-full"
          >
            引き継ぎコードを生成
          </Button>
        </section>
      )}

      {!isLoading && count > 0 && (
        <section className="mb-6" data-testid="memberships-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-slate-900">{t('district_home.your_districts')}</h2>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                isAtLimit
                  ? 'bg-danger-100 text-danger-700'
                  : isNearLimit
                    ? 'bg-warning-100 text-warning-700'
                    : 'bg-slate-100 text-slate-600'
              }`}
              data-testid="memberships-count-badge"
            >
              {count} / {ACCESSIBLE_DISTRICTS_LIMIT}
            </span>
          </div>

          {isNearLimit && (
            <p
              className={`text-xs rounded-md p-2 mb-3 ${
                isAtLimit
                  ? 'bg-danger-50 text-danger-700 border border-danger-200'
                  : 'bg-warning-50 text-warning-700 border border-warning-200'
              }`}
            >
              {isAtLimit
                ? '上限に達しています。21 地区目に参加すると最も古い地区へのアクセス権が一旦外れます (その地区の URL を再度開けば復活)。'
                : '上限に近づいています。21 地区目以降は古い地区から順にアクセス権が外れます (URL から再アクセスで復活)。'}
            </p>
          )}

          <ul className="grid gap-3" data-testid="memberships-list">
            {memberships.map((m) => (
              <li
                key={m.districtId}
                className="border border-slate-200 rounded-lg p-4 bg-white"
                data-testid="membership-row"
              >
                <div className="font-bold text-lg mb-1">📍 {m.districtName}</div>
                {m.nickname && (
                  <p className="text-sm text-slate-600 mb-1">
                    ニックネーム: <span className="font-medium">{m.nickname}</span>
                  </p>
                )}
                {m.joinedSessionCount > 0 && (
                  <p className="text-sm text-slate-600 mb-1">
                    参加セッション: {m.joinedSessionCount} 件
                  </p>
                )}
                {m.lastSeenAt > 0 && (
                  <p className="text-xs text-slate-400 mb-2">
                    最終アクセス: {new Date(m.lastSeenAt).toLocaleDateString('ja-JP')}
                  </p>
                )}
                {/* old2 逐語: オフライン地図を保有する地区には 🗺 badge を表示。 */}
                {offlineMap.districtIds.has(m.districtId) && (
                  <p className="text-xs text-success-700 mb-3" data-testid="offline-map-badge">
                    🗺 オフライン地図あり
                  </p>
                )}
                {m.districtToken ? (
                  <Link
                    to={`/m/${m.districtToken}/map`}
                    className="inline-block bg-primary-600 text-white text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-primary-700"
                    data-testid="membership-link"
                  >
                    {t('district_home.open_district')}
                  </Link>
                ) : (
                  <p className="text-xs text-slate-500">
                    (URL トークンが取得できません。配布された URL から再アクセスしてください)
                  </p>
                )}
              </li>
            ))}
          </ul>

          {/* old2 逐語: 保存中のオフライン地図の合計容量行 (地区数 / 合計 MB / 端末空き GB)。 */}
          {offlineMap.districtIds.size > 0 && (
            <p className="text-xs text-slate-600 mt-3" data-testid="offline-map-total">
              🗺 保存中の地図: {offlineMap.districtIds.size} 地区 / 合計{' '}
              {(offlineMap.totalBytes / 1024 / 1024).toFixed(1)} MB
              {offlineMap.storageEstimate && (
                <>
                  （端末空き{' '}
                  {(
                    (offlineMap.storageEstimate.quotaBytes -
                      offlineMap.storageEstimate.usageBytes) /
                    1024 /
                    1024 /
                    1024
                  ).toFixed(2)}{' '}
                  GB）
                </>
              )}
            </p>
          )}
        </section>
      )}

      {/* PWA standalone でメンバーシップ空: 引継コード入力欄 (旧 L334-366、Sprint 8 component 再利用) */}
      {!isLoading && standalone && count === 0 && auth.status === 'authenticated' && (
        <section className="border-t border-slate-200 pt-6 mb-6" data-testid="consume-section">
          <DeviceTransferConsumeForm />
        </section>
      )}

      <section className="border-t border-slate-200 pt-6">
        <h2 className="font-bold text-slate-900 mb-2">＋ 新しい地区に参加する</h2>
        <p className="text-sm text-slate-700 mb-3">
          配布された <strong>QR コード</strong> または <strong>URL</strong>{' '}
          からアクセスしてください。
        </p>
        {!isLoading && count === 0 && !standalone && (
          <p className="text-xs text-slate-500" data-testid="empty-hint">
            {t('district_home.no_memberships_hint')}
          </p>
        )}
      </section>

      {auth.status === 'authenticated' && (
        <DeviceTransferModal
          open={deviceTransferOpen}
          onClose={() => setDeviceTransferOpen(false)}
        />
      )}
    </main>
  );
}
